import { useEffect, useState } from "react";
import { Phone, Star, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const WhatsAppIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.884 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export function TopBar() {
  const { t } = useLang();
  const [rating, setRating] = useState(null);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/stats/rating-global").then(({ data }) => { if (data.count > 0) setRating(data); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/callbacks", { phone, name });
      toast.success(t(data.message, "Got it! We'll call you back very soon."));
      setOpen(false); setPhone(""); setName("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="h-9 flex items-center" style={{ background: "var(--ink)", color: "var(--bone)" }} data-testid="topbar">
        <div className="max-w-[1400px] w-full mx-auto px-5 md:px-10 flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-4 min-w-0">
            <a href="tel:+33780816014" className="flex items-center gap-1.5 font-semibold whitespace-nowrap" data-testid="topbar-phone-link">
              <Phone size={13} style={{ color: "var(--orange)" }} /> 07 80 81 60 14
            </a>
            <a href="https://wa.me/33776575461" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-semibold whitespace-nowrap" data-testid="topbar-whatsapp-link">
              <span style={{ color: "#25D366" }}><WhatsAppIcon size={13} /></span> <span className="hidden md:inline">WhatsApp</span>
            </a>
            {rating && (
              <span className="hidden sm:flex items-center gap-1.5" data-testid="topbar-rating">
                <span className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={12} fill={i <= Math.round(rating.avg) ? "var(--orange)" : "none"} style={{ color: "var(--orange)" }} />
                  ))}
                </span>
                <span style={{ color: "rgba(245,245,240,0.75)" }}>{String(rating.avg).replace(".", ",")}/5 · {rating.count} {t("avis clients", "reviews")}</span>
              </span>
            )}
          </div>
          <button onClick={() => setOpen(true)} className="font-bold tracking-wide whitespace-nowrap px-3 py-1 transition-colors"
            style={{ background: "var(--orange)", color: "var(--ink)" }} data-testid="topbar-callback-btn">
            {t("Être rappelé gratuitement", "Request a free callback")}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" style={{ background: "rgba(20,20,18,0.6)", backdropFilter: "blur(6px)" }}
          onClick={() => setOpen(false)} data-testid="callback-modal">
          <div className="w-full max-w-md p-8" style={{ background: "var(--bone)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-display font-black text-2xl">{t("On vous rappelle", "We'll call you back")}</h3>
              <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid="callback-close-btn"><X size={20} /></button>
            </div>
            <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
              {t("Laissez votre numéro, un conseiller vous rappelle gratuitement sous 24h ouvrées.", "Leave your number and an advisor will call you back for free within 24 business hours.")}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="callback-form">
              <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder={t("Votre numéro de téléphone *", "Your phone number *")}
                className="w-full px-4 py-3 text-sm" data-testid="callback-phone-input"
                style={{ background: "var(--white)", border: "1px solid var(--border)", outline: "none" }} />
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("Votre nom (optionnel)", "Your name (optional)")}
                className="w-full px-4 py-3 text-sm" data-testid="callback-name-input"
                style={{ background: "var(--white)", border: "1px solid var(--border)", outline: "none" }} />
              <button type="submit" disabled={loading} className="btn-primary w-full py-3 font-bold flex items-center justify-center gap-2" data-testid="callback-submit-btn">
                {loading ? <Loader2 size={17} className="animate-spin" /> : t("Me faire rappeler", "Call me back")}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
